import React, { useEffect, useState } from "react";
import { Shield } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Card, Badge, Avatar, Loading, fmtDate } from "../components/ui";

const ROLES = ["SUPERADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"];
const MATRIX = [
  ["View Employees", [1, 1, 1, 1]], ["Edit Employees", [1, 1, 1, 0]], ["Create Employees", [1, 1, 0, 0]],
  ["Delete Employees", [1, 1, 0, 0]], ["Manage Payroll", [1, 1, 0, 0]], ["Approve Leave", [1, 1, 1, 0]],
  ["Approve Expenses", [1, 1, 1, 0]], ["Manage Recruitment", [1, 1, 0, 0]], ["Manage Users", [1, 0, 0, 0]],
  ["View Reports", [1, 1, 1, 0]],
];

export default function Admin() {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState(null);
  const [logs, setLogs] = useState([]);
  const [denied, setDenied] = useState(false);

  const load = () => {
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch((e) => { if (e.response?.status === 403) setDenied(true); });
    api.get("/audit-logs").then(({ data }) => setLogs(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const setRole = async (id, role) => { await api.put(`/admin/users/${id}`, { role }); load(); };
  const toggle = async (id, active) => { await api.put(`/admin/users/${id}`, { active: !active }); load(); };
  if (denied) return (
    <div>
      <PageHeader title="Administration" />
      <Card className="flex flex-col items-center justify-center px-6 py-20 text-center" data-testid="access-denied">
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500/10 text-red-500"><Shield size={26} /></div>
        <h2 className="font-head text-xl font-extrabold text-slate-900 dark:text-white">Access restricted</h2>
        <p className="mt-2 max-w-sm text-sm text-slate-500 dark:text-slate-400">Administration is available to Super Admins only.</p>
      </Card>
    </div>
  );
  if (!users) return <Loading />;

  return (
    <div>
      <PageHeader title="Administration" subtitle="Manage users, roles and audit trail" />
      <div className="mb-6 flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {[["users", "Users"], ["rbac", "RBAC Matrix"], ["audit", "Audit Log"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-accent text-accent" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>{l}</button>
        ))}
      </div>

      {tab === "users" && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead><tr><th className="th">User</th><th className="th">Email</th><th className="th">Role</th><th className="th">Status</th><th className="th">Action</th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="td"><div className="flex items-center gap-3"><Avatar name={u.employeeName || u.email} size={32} /><span className="font-medium text-slate-800 dark:text-slate-100">{u.employeeName || "—"}</span></div></td>
                    <td className="td">{u.email}</td>
                    <td className="td"><select value={u.role} onChange={(e) => setRole(u.id, e.target.value)} data-testid={`role-${u.id}`} className="rounded border border-slate-200 bg-transparent px-2 py-1 text-xs dark:border-slate-700">{ROLES.map((r) => <option key={r}>{r}</option>)}</select></td>
                    <td className="td"><Badge value={u.active === false ? "INACTIVE" : "ACTIVE"} /></td>
                    <td className="td"><button onClick={() => toggle(u.id, u.active !== false)} className="text-xs font-semibold text-accent hover:underline" data-testid={`toggle-${u.id}`}>{u.active === false ? "Enable" : "Disable"}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "rbac" && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="rbac-table">
              <thead><tr><th className="th">Permission</th>{ROLES.map((r) => <th key={r} className="th text-center">{r}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {MATRIX.map(([perm, vals]) => (
                  <tr key={perm}><td className="td font-medium text-slate-800 dark:text-slate-100">{perm}</td>
                    {vals.map((v, i) => <td key={i} className="td text-center">{v ? <span className="text-emerald-500">✓</span> : <span className="text-slate-300 dark:text-slate-700">—</span>}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "audit" && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="audit-table">
              <thead><tr><th className="th">Action</th><th className="th">Entity</th><th className="th">User</th><th className="th">Details</th><th className="th">Time</th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {logs.map((l) => (
                  <tr key={l.id}><td className="td"><Badge tone="info">{l.action}</Badge></td><td className="td">{l.entity}</td><td className="td">{l.userEmail}</td><td className="td">{l.details}</td><td className="td text-slate-400">{fmtDate(l.createdAt)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
